// Pure halves of web's location-display.tsx, shared so native's event sheet
// builds the identical maps link. The web component (tooltip chrome) and the
// picker serialization stay in web/src/components/ui/location-display.tsx.

export type LocationData = {
  name: string;
  address: string;
  coordinates?: { lat: number; lng: number };
  placeId?: string;
};

/**
 * The persisted location may be a JSON blob (`{name, address, ...}`) when a
 * place was resolved (api `formatResolvedLocation`), or plain text when the
 * user typed freely. Returns the parsed blob, or null for plain text.
 */
export const parseLocationData = (location: string): LocationData | null => {
  try {
    const parsed: unknown = JSON.parse(location);
    // Field-by-field validation rather than a type assertion: the location
    // string comes from external calendars, so valid JSON with the wrong
    // shape must fall back to plain text instead of crashing the render.
    if (
      typeof parsed === "object" &&
      parsed !== null &&
      "name" in parsed &&
      typeof parsed.name === "string" &&
      parsed.name !== "" &&
      "address" in parsed &&
      typeof parsed.address === "string" &&
      parsed.address !== ""
    ) {
      const coordinates =
        "coordinates" in parsed &&
        typeof parsed.coordinates === "object" &&
        parsed.coordinates !== null &&
        "lat" in parsed.coordinates &&
        typeof parsed.coordinates.lat === "number" &&
        "lng" in parsed.coordinates &&
        typeof parsed.coordinates.lng === "number"
          ? { lat: parsed.coordinates.lat, lng: parsed.coordinates.lng }
          : undefined;
      const placeId =
        "placeId" in parsed && typeof parsed.placeId === "string" ? parsed.placeId : undefined;
      return { name: parsed.name, address: parsed.address, coordinates, placeId };
    }
  } catch {
    // Not JSON - this is expected for plain text locations
  }
  return null;
};

// "Kaiser in San Jose, San Jose, CA, USA" -> "Kaiser in San Jose"
const simplifyName = (name: string, address: string): string => name.replace(`, ${address}`, "");

/**
 * Human-readable string for a stored location. We want the user to see the
 * same string the non-editing display shows, never the raw JSON.
 */
export const getLocationDisplayValue = (location: string): string => {
  if (!location) return "";
  const parsed = parseLocationData(location);
  if (parsed) {
    return simplifyName(parsed.name, parsed.address);
  }
  return location;
};

/**
 * Google Maps URL for a stored location, preferring the most precise handle
 * available: place id, then coordinates, then a text search on the address
 * (or on the raw string for plain-text locations).
 */
export const getLocationMapsUrl = (location: string): string => {
  const locationData = parseLocationData(location);
  if (locationData) {
    if (locationData.placeId) {
      return `https://www.google.com/maps/place/?q=place_id:${encodeURIComponent(locationData.placeId)}`;
    }
    if (locationData.coordinates) {
      return `https://www.google.com/maps?q=${locationData.coordinates.lat},${locationData.coordinates.lng}`;
    }
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(locationData.address)}`;
  }
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(location)}`;
};
